import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowUpDown, Package, Plus, Search, ShoppingCart, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createInventoryItem,
  createInventoryMovement,
  createPurchase,
  createSupplier,
  listInventoryItems,
  listInventoryMovements,
  listLowStockItems,
  listPurchases,
  listSuppliers
} from '../api/inventory';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import { LoadingState } from '../components/PageState';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { currency, dateTime } from '../utils/format';

const tabs = [
  { id: 'items', label: 'Bodega', icon: Package },
  { id: 'suppliers', label: 'Proveedores', icon: Truck },
  { id: 'purchases', label: 'Compras', icon: ShoppingCart },
  { id: 'movements', label: 'Movimientos', icon: ArrowUpDown }
];

const emptyItem = {
  type: 'supply',
  name: '',
  sku: '',
  unit: 'unidad',
  minimum_stock: 0,
  cost_price: 0,
  sale_price: '',
  is_sellable: false,
  description: ''
};

const emptySupplier = {
  name: '',
  rut: '',
  email: '',
  phone: '',
  address: ''
};

const emptyPurchase = {
  supplier_id: '',
  purchase_date: new Date().toISOString().slice(0, 10),
  document_type: '',
  document_number: '',
  tax: 0,
  notes: '',
  details: [{ inventory_item_id: '', quantity: 1, unit_cost: 0, sync_marketplace_stock: false }]
};

const emptyMovement = {
  inventory_item_id: '',
  movement_type: 'adjustment_in',
  quantity: 1,
  notes: ''
};

const Inventario = () => {
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [itemType, setItemType] = useState('');
  const [itemOpen, setItemOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase);
  const [movementForm, setMovementForm] = useState(emptyMovement);
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemData, lowStockData, supplierData, purchaseData, movementData] = await Promise.all([
        listInventoryItems({ search: debouncedSearch || undefined, type: itemType || undefined, limit: 100 }),
        listLowStockItems({ limit: 50 }),
        listSuppliers({ limit: 100 }),
        listPurchases({ limit: 100 }),
        listInventoryMovements({ limit: 100 })
      ]);
      setItems(itemData);
      setLowStock(lowStockData);
      setSuppliers(supplierData);
      setPurchases(purchaseData);
      setMovements(movementData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar inventario'));
    } finally {
      setLoading(false);
    }
  }, [itemType, debouncedSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => ({
    items: items.length,
    supplies: items.filter((item) => item.type === 'supply').length,
    products: items.filter((item) => item.type === 'product').length,
    low: lowStock.length
  }), [items, lowStock]);

  const updatePurchaseDetail = (index, field, value) => {
    setPurchaseForm((current) => ({
      ...current,
      details: current.details.map((detail, detailIndex) => (
        detailIndex === index ? { ...detail, [field]: value } : detail
      ))
    }));
  };

  const purchaseSubtotal = purchaseForm.details.reduce((sum, detail) => {
    return sum + (Number(detail.quantity || 0) * Number(detail.unit_cost || 0));
  }, 0);

  const resetModals = () => {
    setItemOpen(false);
    setSupplierOpen(false);
    setPurchaseOpen(false);
    setMovementOpen(false);
    setItemForm(emptyItem);
    setSupplierForm(emptySupplier);
    setPurchaseForm(emptyPurchase);
    setMovementForm(emptyMovement);
  };

  const saveItem = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await createInventoryItem({
        ...itemForm,
        minimum_stock: Number(itemForm.minimum_stock || 0),
        cost_price: Number(itemForm.cost_price || 0),
        sale_price: itemForm.sale_price === '' ? null : Number(itemForm.sale_price || 0)
      });
      toast.success('Ítem creado');
      resetModals();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo crear el ítem'));
    } finally {
      setSaving(false);
    }
  };

  const saveSupplier = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await createSupplier(supplierForm);
      toast.success('Proveedor creado');
      resetModals();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo crear el proveedor'));
    } finally {
      setSaving(false);
    }
  };

  const savePurchase = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await createPurchase({
        ...purchaseForm,
        supplier_id: purchaseForm.supplier_id || null,
        subtotal: purchaseSubtotal,
        total: purchaseSubtotal + Number(purchaseForm.tax || 0),
        details: purchaseForm.details.map((detail) => ({
          inventory_item_id: Number(detail.inventory_item_id),
          quantity: Number(detail.quantity || 0),
          unit_cost: Number(detail.unit_cost || 0),
          sync_marketplace_stock: Boolean(detail.sync_marketplace_stock)
        }))
      });
      toast.success('Compra registrada y stock actualizado');
      resetModals();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo registrar la compra'));
    } finally {
      setSaving(false);
    }
  };

  const saveMovement = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      await createInventoryMovement({
        ...movementForm,
        inventory_item_id: Number(movementForm.inventory_item_id),
        quantity: Number(movementForm.quantity || 0),
        reference_type: 'manual_adjustment'
      });
      toast.success('Movimiento registrado');
      resetModals();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo registrar el movimiento'));
    } finally {
      setSaving(false);
    }
  };

  const itemColumns = [
    {
      key: 'name',
      label: 'Ítem',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-950">{row.name}</p>
          <p className="text-xs text-gray-500">{row.sku || 'Sin SKU'} · {row.unit}</p>
        </div>
      )
    },
    { key: 'type', label: 'Tipo', render: (row) => row.type === 'supply' ? 'Insumo' : 'Producto tienda' },
    {
      key: 'current_stock',
      label: 'Stock',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-950">{Number(row.current_stock || 0)} {row.unit}</p>
          {Number(row.current_stock || 0) <= Number(row.minimum_stock || 0) && (
            <p className="text-xs text-red-700">Bajo mínimo {Number(row.minimum_stock || 0)}</p>
          )}
        </div>
      )
    },
    { key: 'cost_price', label: 'Costo', render: (row) => currency(row.cost_price) },
    { key: 'sale_price', label: 'Venta', render: (row) => row.sale_price === null ? '-' : currency(row.sale_price) },
    { key: 'status', label: 'Estado', render: (row) => <StatusBadge label={row.status === 'active' ? 'Activo' : 'Inactivo'} color={row.status === 'active' ? 'green' : 'slate'} /> }
  ];

  const supplierColumns = [
    { key: 'name', label: 'Proveedor', render: (row) => <span className="font-semibold text-gray-950">{row.name}</span> },
    { key: 'rut', label: 'RUT', render: (row) => row.rut || '-' },
    { key: 'email', label: 'Email', render: (row) => row.email || '-' },
    { key: 'phone', label: 'Teléfono', render: (row) => row.phone || '-' },
    { key: 'status', label: 'Estado', render: (row) => <StatusBadge label={row.status === 'active' ? 'Activo' : 'Inactivo'} color={row.status === 'active' ? 'green' : 'slate'} /> }
  ];

  const purchaseColumns = [
    { key: 'purchase_date', label: 'Fecha' },
    { key: 'supplier_name', label: 'Proveedor', render: (row) => row.supplier_name || '-' },
    { key: 'document_number', label: 'Documento', render: (row) => [row.document_type, row.document_number].filter(Boolean).join(' ') || '-' },
    { key: 'total', label: 'Total', render: (row) => <span className="font-semibold text-gray-950">{currency(row.total)}</span> },
    { key: 'status', label: 'Estado', render: (row) => <StatusBadge label={row.status} color={row.status === 'received' ? 'green' : 'slate'} /> },
    { key: 'created_at', label: 'Creada', render: (row) => dateTime(row.created_at) }
  ];

  const movementColumns = [
    { key: 'created_at', label: 'Fecha', render: (row) => dateTime(row.created_at) },
    { key: 'item_name', label: 'Ítem', render: (row) => row.item_name || `#${row.inventory_item_id}` },
    { key: 'movement_type', label: 'Movimiento' },
    { key: 'quantity', label: 'Cantidad', render: (row) => Number(row.quantity || 0) },
    { key: 'previous_stock', label: 'Antes', render: (row) => Number(row.previous_stock || 0) },
    { key: 'new_stock', label: 'Después', render: (row) => Number(row.new_stock || 0) },
    { key: 'notes', label: 'Notas', render: (row) => row.notes || '-' }
  ];

  const openAction = {
    items: () => setItemOpen(true),
    suppliers: () => setSupplierOpen(true),
    purchases: () => setPurchaseOpen(true),
    movements: () => setMovementOpen(true)
  }[activeTab];

  const actionLabel = {
    items: 'Nuevo ítem',
    suppliers: 'Nuevo proveedor',
    purchases: 'Registrar compra',
    movements: 'Ajuste stock'
  }[activeTab];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compras y Bodega"
        description="Inventario de tienda e insumos clínicos, compras, stock mínimo y movimientos."
        actions={<Button icon={Plus} onClick={openAction}>{actionLabel}</Button>}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Ítems</p>
          <p className="mt-1 text-2xl font-semibold text-gray-950">{totals.items}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Insumos</p>
          <p className="mt-1 text-2xl font-semibold text-gray-950">{totals.supplies}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Productos tienda</p>
          <p className="mt-1 text-2xl font-semibold text-gray-950">{totals.products}</p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-sm">Bajo stock</p>
          </div>
          <p className="mt-1 text-2xl font-semibold text-red-950">{totals.low}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-2">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === 'items' && (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid gap-3 md:grid-cols-[1fr_220px_auto] md:items-end">
            <FormField label="Buscar">
              <input className={inputClassName} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, SKU..." />
            </FormField>
            <FormField label="Tipo">
              <select className={inputClassName} value={itemType} onChange={(event) => setItemType(event.target.value)}>
                <option value="">Todos</option>
                <option value="supply">Insumos</option>
                <option value="product">Productos tienda</option>
              </select>
            </FormField>
            <Button variant="secondary" icon={Search} onClick={loadData}>Filtrar</Button>
          </div>
        </section>
      )}

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : (
          <>
            {activeTab === 'items' && <Table columns={itemColumns} data={items} emptyMessage="No hay ítems de inventario" />}
            {activeTab === 'suppliers' && <Table columns={supplierColumns} data={suppliers} emptyMessage="No hay proveedores" />}
            {activeTab === 'purchases' && <Table columns={purchaseColumns} data={purchases} emptyMessage="No hay compras registradas" />}
            {activeTab === 'movements' && <Table columns={movementColumns} data={movements} emptyMessage="No hay movimientos" />}
          </>
        )}
      </section>

      <Modal open={itemOpen} title="Nuevo ítem de inventario" onClose={resetModals} footer={(
        <>
          <Button variant="secondary" onClick={resetModals}>Cancelar</Button>
          <Button type="submit" form="inventory-item-form" disabled={saving}>Guardar</Button>
        </>
      )}>
        <form id="inventory-item-form" className="space-y-4" onSubmit={saveItem}>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Tipo">
              <select className={inputClassName} value={itemForm.type} onChange={(event) => setItemForm({ ...itemForm, type: event.target.value })}>
                <option value="supply">Insumo clínico</option>
                <option value="product">Producto tienda</option>
              </select>
            </FormField>
            <FormField label="Unidad">
              <input className={inputClassName} value={itemForm.unit} onChange={(event) => setItemForm({ ...itemForm, unit: event.target.value })} />
            </FormField>
          </div>
          <FormField label="Nombre">
            <input className={inputClassName} required value={itemForm.name} onChange={(event) => setItemForm({ ...itemForm, name: event.target.value })} />
          </FormField>
          <div className="grid gap-3 md:grid-cols-3">
            <FormField label="SKU">
              <input className={inputClassName} value={itemForm.sku} onChange={(event) => setItemForm({ ...itemForm, sku: event.target.value })} />
            </FormField>
            <FormField label="Stock mínimo">
              <input className={inputClassName} type="number" min="0" step="0.01" value={itemForm.minimum_stock} onChange={(event) => setItemForm({ ...itemForm, minimum_stock: event.target.value })} />
            </FormField>
            <FormField label="Costo">
              <input className={inputClassName} type="number" min="0" step="1" value={itemForm.cost_price} onChange={(event) => setItemForm({ ...itemForm, cost_price: event.target.value })} />
            </FormField>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Precio venta">
              <input className={inputClassName} type="number" min="0" step="1" value={itemForm.sale_price} onChange={(event) => setItemForm({ ...itemForm, sale_price: event.target.value })} />
            </FormField>
            <label className="flex items-center gap-2 pt-6 text-sm text-gray-700">
              <input type="checkbox" checked={itemForm.is_sellable} onChange={(event) => setItemForm({ ...itemForm, is_sellable: event.target.checked })} />
              Disponible para vender
            </label>
          </div>
          <FormField label="Descripción">
            <textarea className={inputClassName} rows={3} value={itemForm.description} onChange={(event) => setItemForm({ ...itemForm, description: event.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal open={supplierOpen} title="Nuevo proveedor" onClose={resetModals} footer={(
        <>
          <Button variant="secondary" onClick={resetModals}>Cancelar</Button>
          <Button type="submit" form="supplier-form" disabled={saving}>Guardar</Button>
        </>
      )}>
        <form id="supplier-form" className="space-y-4" onSubmit={saveSupplier}>
          <FormField label="Nombre">
            <input className={inputClassName} required value={supplierForm.name} onChange={(event) => setSupplierForm({ ...supplierForm, name: event.target.value })} />
          </FormField>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="RUT">
              <input className={inputClassName} value={supplierForm.rut} onChange={(event) => setSupplierForm({ ...supplierForm, rut: event.target.value })} />
            </FormField>
            <FormField label="Teléfono">
              <input className={inputClassName} value={supplierForm.phone} onChange={(event) => setSupplierForm({ ...supplierForm, phone: event.target.value })} />
            </FormField>
          </div>
          <FormField label="Email">
            <input className={inputClassName} type="email" value={supplierForm.email} onChange={(event) => setSupplierForm({ ...supplierForm, email: event.target.value })} />
          </FormField>
          <FormField label="Dirección">
            <input className={inputClassName} value={supplierForm.address} onChange={(event) => setSupplierForm({ ...supplierForm, address: event.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal open={purchaseOpen} title="Registrar compra" onClose={resetModals} size="lg" footer={(
        <>
          <Button variant="secondary" onClick={resetModals}>Cancelar</Button>
          <Button type="submit" form="purchase-form" disabled={saving}>Registrar compra</Button>
        </>
      )}>
        <form id="purchase-form" className="space-y-4" onSubmit={savePurchase}>
          <div className="grid gap-3 md:grid-cols-3">
            <FormField label="Proveedor">
              <select className={inputClassName} value={purchaseForm.supplier_id} onChange={(event) => setPurchaseForm({ ...purchaseForm, supplier_id: event.target.value })}>
                <option value="">Sin proveedor</option>
                {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
              </select>
            </FormField>
            <FormField label="Fecha">
              <input className={inputClassName} type="date" value={purchaseForm.purchase_date} onChange={(event) => setPurchaseForm({ ...purchaseForm, purchase_date: event.target.value })} />
            </FormField>
            <FormField label="Documento">
              <input className={inputClassName} value={purchaseForm.document_number} onChange={(event) => setPurchaseForm({ ...purchaseForm, document_number: event.target.value })} placeholder="Factura / Boleta" />
            </FormField>
          </div>
          <div className="space-y-3">
            {purchaseForm.details.map((detail, index) => (
              <div key={index} className="grid gap-3 rounded-md border border-gray-200 p-3 md:grid-cols-[1fr_120px_140px_170px_auto] md:items-end">
                <FormField label="Ítem">
                  <select className={inputClassName} required value={detail.inventory_item_id} onChange={(event) => updatePurchaseDetail(index, 'inventory_item_id', event.target.value)}>
                    <option value="">Selecciona</option>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.unit}){item.marketplace_product_id || item.is_sellable ? ' · tienda' : ''}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Cantidad">
                  <input className={inputClassName} required type="number" min="0.01" step="0.01" value={detail.quantity} onChange={(event) => updatePurchaseDetail(index, 'quantity', event.target.value)} />
                </FormField>
                <FormField label="Costo unitario">
                  <input className={inputClassName} required type="number" min="0" step="1" value={detail.unit_cost} onChange={(event) => updatePurchaseDetail(index, 'unit_cost', event.target.value)} />
                </FormField>
                <label className="flex min-h-[42px] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(detail.sync_marketplace_stock)}
                    onChange={(event) => updatePurchaseDetail(index, 'sync_marketplace_stock', event.target.checked)}
                  />
                  Sumar tienda
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setPurchaseForm((current) => ({
                    ...current,
                    details: current.details.filter((_, detailIndex) => detailIndex !== index)
                  }))}
                  disabled={purchaseForm.details.length === 1}
                >
                  Quitar
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPurchaseForm((current) => ({
              ...current,
              details: [...current.details, { inventory_item_id: '', quantity: 1, unit_cost: 0, sync_marketplace_stock: false }]
            }))}
          >
            Agregar línea
          </Button>
          <div className="grid gap-3 md:grid-cols-3">
            <FormField label="Subtotal">
              <input className={inputClassName} value={currency(purchaseSubtotal)} disabled />
            </FormField>
            <FormField label="Impuesto">
              <input className={inputClassName} type="number" min="0" step="1" value={purchaseForm.tax} onChange={(event) => setPurchaseForm({ ...purchaseForm, tax: event.target.value })} />
            </FormField>
            <FormField label="Total">
              <input className={inputClassName} value={currency(purchaseSubtotal + Number(purchaseForm.tax || 0))} disabled />
            </FormField>
          </div>
          <FormField label="Notas">
            <textarea className={inputClassName} rows={3} value={purchaseForm.notes} onChange={(event) => setPurchaseForm({ ...purchaseForm, notes: event.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal open={movementOpen} title="Ajuste manual de stock" onClose={resetModals} footer={(
        <>
          <Button variant="secondary" onClick={resetModals}>Cancelar</Button>
          <Button type="submit" form="movement-form" disabled={saving}>Registrar ajuste</Button>
        </>
      )}>
        <form id="movement-form" className="space-y-4" onSubmit={saveMovement}>
          <FormField label="Ítem">
            <select className={inputClassName} required value={movementForm.inventory_item_id} onChange={(event) => setMovementForm({ ...movementForm, inventory_item_id: event.target.value })}>
              <option value="">Selecciona</option>
              {items.map((item) => <option key={item.id} value={item.id}>{item.name} · stock {Number(item.current_stock || 0)}</option>)}
            </select>
          </FormField>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Tipo">
              <select className={inputClassName} value={movementForm.movement_type} onChange={(event) => setMovementForm({ ...movementForm, movement_type: event.target.value })}>
                <option value="adjustment_in">Ajuste entrada</option>
                <option value="adjustment_out">Ajuste salida</option>
                <option value="cancellation">Reversa / cancelación</option>
              </select>
            </FormField>
            <FormField label="Cantidad">
              <input className={inputClassName} required type="number" min="0.01" step="0.01" value={movementForm.quantity} onChange={(event) => setMovementForm({ ...movementForm, quantity: event.target.value })} />
            </FormField>
          </div>
          <FormField label="Notas">
            <textarea className={inputClassName} rows={3} value={movementForm.notes} onChange={(event) => setMovementForm({ ...movementForm, notes: event.target.value })} />
          </FormField>
        </form>
      </Modal>
    </div>
  );
};

export default Inventario;
